import React, { useCallback, useState } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  Box, HStack, ScrollView, Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { ReportFilterBar } from '@/admin/common/reportFilters/ReportFilterBar';

import { DashboardEmptyState } from './DashboardEmptyState';
import { DashboardError } from './DashboardError';
import { DashboardShimmer, DashboardSkeleton } from './DashboardLoadingPlaceholder';
import type { UseDashboardResult } from './useDashboard';
import { useReduceMotionEnabled } from './useReduceMotionEnabled';

const PAGE_MAX_WIDTH = 1048;
const HAIRLINE = '#EAECF0';

export type DashboardPageProps = {
  dashboard: UseDashboardResult;
  /** Top-right control, beside the session count. */
  headerEnd?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * Shared dashboard shell: title row, filter bar, and status regions.
 */
export function DashboardPage({ dashboard, headerEnd, children }: DashboardPageProps) {
  const { t } = useTranslation();
  const reduceMotion = useReduceMotionEnabled();
  const [filterScrolled, setFilterScrolled] = useState(false);
  const {
    definition,
    appliedFilters,
    urlParamKeys,
    setFilter,
    resetFilters,
    status,
    refetch,
    scopeFigure,
  } = dashboard;

  const useLast90DaysPreset = !urlParamKeys.includes('from') && !urlParamKeys.includes('to');

  const showScope = scopeFigure?.value != null;
  const sessionCount = scopeFigure?.extras?.n;
  const studentCount = scopeFigure?.extras?.nStudents;

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset: { y } } = event.nativeEvent;
    setFilterScrolled(y > 100);
  }, []);

  return (
    <ScrollView
      flex={1}
      testID="dashboard-scroll"
      onScroll={onScroll}
      scrollEventThrottle={16}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      <Box
        flex={1}
        width="100%"
        maxWidth={PAGE_MAX_WIDTH}
        alignSelf="center"
        bg="$bgCard"
        px={40}
        pt={32}
        pb={48}
        testID="dashboard-page"
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          space="sm"
          position="relative"
          zIndex={40}
        >
          <Text
            testID="dashboard-title"
            fontFamily="$heading"
            color="$textHeading"
            sx={{ fontSize: 24, lineHeight: 32, fontWeight: '600' }}
          >
            {t(definition.titleKey)}
          </Text>
          <HStack space="md" alignItems="center">
            {!!showScope && sessionCount != null && studentCount != null && (
              <Text size="sm" color="$textSubtle" testID="dashboard-scope-count">
                {t('admin.analytics.scopeCount', { sessions: sessionCount, students: studentCount })}
              </Text>
            )}
            {headerEnd}
          </HStack>
        </HStack>
        <Text size="sm" color="$textSubtle" mt="$1">{t(definition.subtitleKey)}</Text>

        <Box
          mt="$4"
          top={0}
          zIndex={30}
          bg="$bgCard"
          testID="dashboard-sticky-filter-row"
          sx={{
            _web: { position: 'sticky', top: 0 },
          }}
          style={{
            borderBottomWidth: filterScrolled ? 1 : 0,
            borderBottomColor: HAIRLINE,
          }}
        >
          <ReportFilterBar
            filters={appliedFilters}
            filterNames={definition.filterNames}
            setFilter={setFilter}
            useLast90DaysPreset={useLast90DaysPreset}
            disabled={status === 'loading'}
          />
        </Box>

        {status === 'loading' && (
          reduceMotion
            ? <DashboardSkeleton />
            : <DashboardShimmer />
        )}

        {status === 'error' && (
          <DashboardError onRetry={() => refetch()} onResetFilters={resetFilters} />
        )}

        {status === 'empty' && <DashboardEmptyState onResetFilters={resetFilters} />}

        {status === 'ready' && children}
      </Box>
    </ScrollView>
  );
}
